import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { useState } from 'react';

const faqs = [
  {
    question: 'Are videos better than blogs?',
    answer: 'According to studies, when watching a video, viewers recall 95% of the material, while when reading text, they only retain 10%. Video content may raise brand visibility, enhance conversions, and significantly increase audience interest.',
  },
  {
    question: 'How will videos help with SEO?',
    answer: 'Our videos are SEO-optimized, enhancing your content\'s visibility. They complement your written content, potentially boosting your search rankings and attracting a wider audience.',
  },
  {
    question: 'Is my data secure on your platform?',
    answer: 'Yes, security is a top priority. We use industry-leading encryption and secure data storage methods to protect your information, ensuring that your data is safe from unauthorized access.',
  },
  {
    question: 'Will it save me time?',
    answer: 'Definitely! Our AI-powered system automates the video creation process, significantly reducing the time and effort needed to produce engaging video content. More content, less work!',
  },
  {
    question: 'How will this boost engagement?',
    answer: 'Videos are highly engaging and shareable. By converting your blogs to dynamic videos, you\'re likely to see increased viewer retention, higher social media engagement, and potentially wider reach across platforms.',
  },
  {
    question: 'What kind of support is available if I need help?',
    answer: 'We offer 24/7 customer support via live chat, email, and phone. Additionally, our platform includes a comprehensive help center with tutorials, FAQs, and user guides to assist you.',
  },
  {
    question: 'Is it complicated to use?',
    answer: 'Not at all! Our platform is designed for simplicity. With an intuitive interface and automated processes, you can convert your content to video in just a few clicks. No technical expertise required!',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => {
  return (
    <div className="border-b border-white/10">
      <button
        onClick={onToggle}
        className="w-full py-6 flex items-center justify-between text-left group"
        data-testid={`faq-question-${faq.question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-lg font-semibold text-white group-hover:text-brand-primary transition-colors pr-4">
          {faq.question}
        </span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
          isOpen ? 'bg-brand-primary' : 'bg-surface-elevated'
        }`}>
          {isOpen ? (
            <Minus className="w-4 h-4 text-surface-base" />
          ) : (
            <Plus className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-gray-400 leading-relaxed pr-12">
          {faq.answer}
        </p>
      </motion.div>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-24 px-6" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            Frequently Asked Questions
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Wondering About Something?
            <br />
            <span className="text-gray-400">Let's Clear Things Up!</span>
          </h2>
          <p className="text-gray-400 text-lg">
            We've gathered all the important info right here. Explore our FAQs and find the answers you need.
          </p>
        </motion.div>

        {/* FAQ List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
